import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    Step,
    StepLabel,
    Stepper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import FileDropZone from "../FileDropZone/FileDropZone";

const getOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    return option.display_name || option.label || option.category_name || option.machine_label || "";
};

const MAX_UPLOAD_FILES = 10;

const AdminDocumentUploadModal = ({
    open,
    onClose,
    dropzoneKey = 0,
    formData,
    setFormData,
    machineOptions = [],
    machineLoading = false,
    selectedMachine = null,
    onMachineChange,
    onMachineSearchChange,
    categoryOptions = [],
    categoriesLoading = false,
    selectedCategory = null,
    onCategoryChange,
    onCategorySearchChange = () => {},
    onSubmit,
    submitting = false,
    errorMessage = "",
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [localErrors, setLocalErrors] = useState([]);
    const files = Array.isArray(formData?.files) ? formData.files : [];
    const selectedFileCount = files.length;
    const selectedCategoryId = Number(selectedCategory?.id || selectedCategory?.category_id);
    const hasSelectedCategory = Number.isFinite(selectedCategoryId) && selectedCategoryId > 0;
    const hasSelectedMachine = Number.isFinite(Number(selectedMachine?.machine_id)) && Number(selectedMachine?.machine_id) > 0;
    const selectedCategoryLabel = getOptionLabel(selectedCategory);
    const steps = useMemo(
        () => [
            t("GENERAL.DOCUMENT_DETAILS", { defaultValue: "Document Details" }),
            t("GENERAL.MACHINE_MAPPING", { defaultValue: "Machine Mapping" }),
        ],
        [t]
    );

    useEffect(() => {
        if (!open) return;
        setActiveStep(0);
        setLocalErrors([]);
    }, [open, dropzoneKey]);

    const validateStepOne = () => {
        const messages = [];
        if (selectedFileCount === 0) {
            messages.push(t("MESSAGES.SELECT_FILE_TO_UPLOAD", { defaultValue: "Please select at least one file to upload" }));
        }
        if (!hasSelectedCategory) {
            messages.push(t("MESSAGES.SELECT_CATEGORY_FROM_DATABASE", { defaultValue: "Please select a category from the database" }));
        }

        setLocalErrors(messages);
        return messages.length === 0;
    };

    const validateStepTwo = () => {
        const messages = [];
        if (!hasSelectedMachine) {
            messages.push(t("MESSAGES.SELECT_MACHINE", { defaultValue: "Please select a machine" }));
        }

        setLocalErrors(messages);
        return messages.length === 0;
    };

    const handleClose = () => {
        if (submitting) return;
        setActiveStep(0);
        setLocalErrors([]);
        onClose();
    };

    const handleFileChange = (updater) => {
        setFormData(updater);
        setLocalErrors([]);
    };

    const handlePrimaryAction = () => {
        if (activeStep === 0) {
            if (validateStepOne()) {
                setActiveStep(1);
            }
            return;
        }

        if (!validateStepOne()) {
            setActiveStep(0);
            return;
        }
        if (!validateStepTwo()) return;

        setLocalErrors([]);
        onSubmit();
    };

    return (
        <ModalWrapper
            open={open}
            setOpen={() => {}}
            onClose={handleClose}
            width="60vw"
            title={t("BUTTON.ADD_DOCUMENT", { defaultValue: "Add Document" })}
            subtitle={t("MESSAGES.ADMIN_UPLOAD_DOCUMENT_MODAL_SUBTITLE", {
                defaultValue: "Upload a document and map it to a machine/category in the documentation table.",
            })}
            actions={
                <Box sx={{ display: "flex", justifyContent: activeStep > 0 ? "space-between" : "flex-end", width: "100%", alignItems: "center" }}>
                    {activeStep > 0 ? (
                        <Button onClick={() => setActiveStep((step) => Math.max(step - 1, 0))} color="secondary" disabled={submitting}>
                            {t("BUTTON.BACK", { defaultValue: "Back" })}
                        </Button>
                    ) : null}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", columnGap: 1.5 }}>
                        <Button onClick={handleClose} disabled={submitting}>
                            {t("BUTTON.CANCEL", { defaultValue: "Cancel" })}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handlePrimaryAction}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {activeStep === steps.length - 1
                                ? submitting
                                    ? t("BUTTON.UPLOADING", { defaultValue: "Uploading..." })
                                    : t("BUTTON.UPLOAD", { defaultValue: "Upload" })
                                : t("BUTTON.NEXT", { defaultValue: "Next" })}
                        </Button>
                    </Box>
                </Box>
            }
            content={
                <Stack spacing={2}>
                    {localErrors.length > 0 ? (
                        <Alert severity="error">
                            <Box sx={{ fontWeight: 600, mb: 0.75 }}>
                                {t("UI_TEXT.PLEASE_FIX_THE_FOLLOWING", { defaultValue: "Please fix the following:" })}
                            </Box>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {localErrors.map((message, index) => (
                                    <li key={`${message}-${index}`}>{message}</li>
                                ))}
                            </Box>
                        </Alert>
                    ) : null}

                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                    <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {activeStep === 0 ? (
                        <Stack spacing={2}>
                            <Autocomplete
                                disablePortal
                                options={categoryOptions}
                                value={selectedCategory}
                                loading={categoriesLoading}
                                onChange={(_, value) => {
                                    onCategoryChange(value || null);
                                    setLocalErrors([]);
                                }}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "reset") {
                                        onCategorySearchChange(value);
                                    }
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    `${option?.id || option?.category_id || ""}` === `${value?.id || value?.category_id || ""}`
                                }
                                getOptionLabel={getOptionLabel}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("LABELS.CATEGORY", { defaultValue: "Category" })}
                                        placeholder={t("MESSAGES.SEARCH_CATEGORY_FROM_DATABASE", {
                                            defaultValue: "Search category from database",
                                        })}
                                    />
                                )}
                                noOptionsText={
                                    categoriesLoading
                                        ? t("BUTTON.LOADING", { defaultValue: "Loading..." })
                                        : t("MESSAGES.NO_CATEGORIES_FOUND", { defaultValue: "No categories found" })
                                }
                            />
                            {selectedCategoryLabel ? (
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1.5,
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                        {t("LABELS.SELECTED_CATEGORY", { defaultValue: "Selected Category" })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {selectedCategoryLabel}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t("LABELS.FILE", { defaultValue: "File" })}
                                </Typography>
                                <FileDropZone key={dropzoneKey} setParentData={handleFileChange} maxFiles={MAX_UPLOAD_FILES} />
                                {selectedFileCount > 0 ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                                        {t("MESSAGES.FILES_SELECTED", {
                                            defaultValue: "{{count}} file(s) selected",
                                            count: selectedFileCount,
                                        })}
                                    </Typography>
                                ) : null}
                                <FormHelperText>
                                    {t("MESSAGES.ADMIN_DOCUMENT_SUPPORTED_FILE_HINT", {
                                        defaultValue:
                                            "Upload up to 10 supported document files. Max 500 MB each; HTML max 10 MB; video duration max 2 hours.",
                                    })}
                                </FormHelperText>
                            </Box>
                        </Stack>
                    ) : null}

                    {activeStep === 1 ? (
                        <Stack spacing={2}>
                            <Autocomplete
                                disablePortal
                                options={machineOptions}
                                value={selectedMachine}
                                loading={machineLoading}
                                onChange={(_, value) => {
                                    onMachineChange(value);
                                    setLocalErrors([]);
                                }}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "reset") onMachineSearchChange(value);
                                }}
                                isOptionEqualToValue={(option, value) => Number(option?.machine_id) === Number(value?.machine_id)}
                                getOptionLabel={getOptionLabel}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("LABELS.MACHINE", { defaultValue: "Machine" })}
                                        placeholder={t("MESSAGES.SEARCH_MACHINE_BY_NAME_OR_NUMBER", {
                                            defaultValue: "Search by machine number or name",
                                        })}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {machineLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Alert severity="info">
                                {t("MESSAGES.ADMIN_DOCUMENT_MACHINE_MAPPING_HINT", {
                                    defaultValue: "Select the machine where this document should appear in AMF Method Documentation.",
                                })}
                            </Alert>
                        </Stack>
                    ) : null}
                </Stack>
            }
        />
    );
};

export default AdminDocumentUploadModal;
